/**
 * Result, built from lastFight: banner and method, fighters, the XP breakdown line by line, the
 * level and skill point bars filling, coins, and Fight again / Training / Home. Without rewards
 * (a practice fight offline, D26, or the server's not arrived yet) a short note stands in.
 * Landscape phones: designs/Caged Result Landscape (the main phone layout). Portrait: Caged
 * Result / Caged Result Loss. Desktop: Caged Result Desktop / Result Loss Desktop.
 */
import { addSkillXp, availablePoints, FIGHT, LEAGUES, levelFromXp, nextSkillPointCost, PROGRESSION, type FightRewards } from '@caged/shared';
import { useEffect, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { uiSounds } from '../../audio/samples';
import { useApp, type FightSummary } from '../../state/store';
import { KeepProgress } from '../components/AccountButtons';
import { ArenaBackdrop, Rays } from '../components/Backdrop';
import { SplitBar } from '../components/Bars';
import { DesktopHeader } from '../components/Chrome';
import { FighterArt } from '../components/FighterArt';
import ui from '../components/ui.module.css';
import { clock, formatNumber, recordLabel } from '../format';
import { useEaseIn, useLayoutMode, useReducedMotion, useScrollTop, type LayoutMode } from '../hooks';
import { xpAtLevelStart, xpRows, type XpRow } from '../progression';
import '../ui.css';
import s from './ResultScreen.module.css';

/** Count-up timing (ms), as in the designs. */
const COUNT_DELAY = 700;
const COUNT_DURATION = 1200;

const TIPS = ['guard', 'grapple', 'jab', 'signature'] as const;

/** A fight with rewards to show. */
type Rewarded = FightSummary & { rewards: FightRewards };

interface Derived {
  win: boolean;
  loss: boolean;
  draw: boolean;
  ko: boolean;
  mine: number;
  theirs: number;
}

function derive(f: FightSummary): Derived {
  const mine = f.result.roundWinners.filter((w) => w === 0).length;
  const theirs = f.result.roundWinners.filter((w) => w === 1).length;
  return {
    win: f.outcome === 'win',
    loss: f.outcome === 'loss',
    draw: f.outcome === 'draw',
    ko: f.result.method === 'ko',
    mine,
    theirs,
  };
}

function useLabels(f: FightSummary, d: Derived) {
  const { t } = useTranslation();
  const format = t('screens.ui.format', { rounds: FIGHT.rounds, seconds: FIGHT.roundSeconds });
  const forfeit = f.result.method === 'forfeit';
  const round = f.result.round;
  let banner: string;
  let method: string;
  let meta: string;
  if (forfeit && f.connectionLost) {
    banner = t('screens.result.banner.connectionLost');
    method = t('screens.result.method.connectionLost', { round });
    meta = t('screens.result.meta.forfeit', { round });
  } else if (forfeit) {
    banner = d.win ? t('screens.result.banner.win') : t('screens.result.banner.youLeft');
    method = t(d.win ? 'screens.result.method.oppLeft' : 'screens.result.method.youLeft', { round });
    meta = t('screens.result.meta.forfeit', { round });
  } else if (d.ko) {
    banner = d.win ? t('screens.result.banner.ko') : t('screens.result.banner.knockedOut');
    method = t('screens.result.method.ko', { round, time: clock(f.result.roundTime) });
    meta = t('screens.result.meta.ko', { round });
  } else {
    banner = d.win
      ? t('screens.result.banner.win')
      : d.draw
        ? t('screens.result.banner.draw')
        : t('screens.result.banner.lostOnPoints');
    method = d.draw
      ? t('screens.result.method.draw', { me: d.mine, them: d.theirs })
      : t('screens.result.method.decision', { me: d.mine, them: d.theirs });
    meta = t('screens.result.meta.decision', { format, me: d.mine, them: d.theirs });
  }
  const league = f.match.online?.info.league;
  return { banner, method, meta, mode: league ? t('screens.lobby.leagueFight', { day: league.day }) : t('screens.result.mode') };
}

/**
 * The judges' scorecards, revealed now (D12: nobody is told who won a round during the fight).
 * One chip per round fought: its winner, "Even", or "KO" for the round the fight was stopped in.
 */
function Scorecard({ f }: { f: FightSummary }) {
  const { t } = useTranslation();
  const r = f.result;
  // A KO or a forfeit stops the fight in its round; the rounds before it were scored.
  const stopped = r.method === 'ko' || r.method === 'forfeit';
  const rounds = stopped ? r.round : FIGHT.rounds;
  const chips = Array.from({ length: rounds }, (_, i) => {
    const w = r.roundWinners[i];
    const koRound = stopped && i === r.round - 1;
    const who = w === 0 ? 'you' : w === 1 ? 'opp' : 'even';
    const text = koRound
      ? t(r.method === 'forfeit' ? 'screens.result.scorecard.left' : 'screens.result.scorecard.ko')
      : w === 0
        ? t('screens.result.scorecard.you')
        : w === 1
          ? f.match.opponent.name
          : t('screens.result.scorecard.even');
    return (
      <li key={i} className={s.scoreChip} data-w={who}>
        <span className={s.scoreRound}>{t('screens.result.scorecard.round', { round: i + 1 })}</span>
        <span className={s.scoreWho}>{text}</span>
      </li>
    );
  });
  return (
    <ul className={s.scorecard} aria-label={t('screens.result.scorecard.label')}>
      {chips}
    </ul>
  );
}

function VsLine({ f, d }: { f: FightSummary; d: Derived }) {
  const { t } = useTranslation();
  const record = useApp((st) => st.progress.record);
  const values = { opp: f.match.opponent.name, me: f.match.player.name, record: recordLabel(t, record) };
  const components = { g: <span className={s.green} />, w: <span className={s.white} /> };
  const key = d.win ? 'screens.result.defeated' : d.draw ? 'screens.result.drewWith' : 'screens.result.beatYou';
  return (
    <div className={s.vsLine}>
      <Trans i18nKey={key} values={values} components={components} />
    </div>
  );
}

function Fighters({ f, d, size }: { f: FightSummary; d: Derived; size: 'sm' | 'md' | 'lg' }) {
  const h = { sm: 100, md: 92, lg: 260 }[size];
  const loser = { sm: 48, md: 44, lg: 104 }[size];
  const drawH = { sm: 92, md: 84, lg: 220 }[size];
  const koMe = { sm: 70, md: 80, lg: 170 }[size];
  if (d.draw) {
    return (
      <div className={[s.stage, s.stageDraw].join(' ')}>
        <FighterArt id={f.match.player.fighterId} height={drawH} alt={f.match.player.name} look={f.match.player.look} />
        <FighterArt id={f.match.opponent.fighterId} height={drawH} flip delay={0.7} alt={f.match.opponent.name} look={f.match.opponent.look} />
      </div>
    );
  }
  if (d.loss) {
    return (
      <div className={[s.stage, s.stageLoss].join(' ')}>
        <FighterArt id={f.match.player.fighterId} state="ko" height={koMe} pose="ko" alt={f.match.player.name} look={f.match.player.look} />
      </div>
    );
  }
  return (
    <div className={[s.stage, s.stageWin].join(' ')}>
      <div className={s.loser}>
        <FighterArt id={f.match.opponent.fighterId} state="ko" height={loser} pose="koDim" shadow="soft" alt={f.match.opponent.name} look={f.match.opponent.look} />
      </div>
      <div className={s.winner}>
        <FighterArt id={f.match.player.fighterId} state="win" height={h} pose="win" alt={f.match.player.name} look={f.match.player.look} />
      </div>
    </div>
  );
}

function rowLabel(t: (k: string, o?: Record<string, unknown>) => string, r: XpRow): string {
  switch (r.key) {
    case 'opponent':
      return t(r.value > 1 ? 'screens.vs.strength.tough' : 'screens.vs.strength.easy');
    case 'band':
      return t('screens.result.xp.band', { pct: Math.round(r.value * 100) });
    case 'streak':
      return t('screens.result.xp.streak', { days: PROGRESSION.xp.streakDays });
    default:
      return t(`screens.result.xp.${r.key}`);
  }
}

function XpBreakdown({ f, t01 }: { f: Rewarded; t01: number }) {
  const { t } = useTranslation();
  const rows = xpRows(f.rewards.xp);
  const rested = rows.some((r) => r.key === 'rested');
  const total = Math.round(f.rewards.xp.total * t01);
  return (
    <div className={[ui.glass, s.xpCard].join(' ')}>
      <ul className={s.xpList} aria-label={t('screens.result.xp.title')}>
        {rows.map((r, i) => {
          const sub =
            r.kind === 'mul'
              ? `${formatNumber(r.from ?? 0)} × ${formatNumber(r.value)} = ${formatNumber(r.to ?? 0)}`
              : r.bonus
                ? t(r.key === 'streak' ? 'screens.result.xp.streakSub' : 'screens.result.xp.dailySub') +
                  (rested ? ` · ${t('screens.result.xp.notDoubled')}` : '')
                : '';
          return (
            <li key={`${r.key}${i}`} className={s.xpRow} style={{ animationDelay: `${0.45 + i * 0.08}s` }}>
              <span className={s.xpText}>
                <span className={s.xpLabel}>{rowLabel(t, r)}</span>
                {sub && <span className={s.xpSub}>{sub}</span>}
              </span>
              <span className={[s.xpVal, s[r.tone]].join(' ')}>{r.kind === 'mul' ? `×${formatNumber(r.value)}` : `+${formatNumber(r.value)}`}</span>
            </li>
          );
        })}
      </ul>
      <div className={s.xpTotal}>
        <span className={s.xpTotalLabel}>{t('screens.result.xp.total')}</span>
        <span className={s.xpTotalVal} aria-label={t('screens.result.xp.totalAria', { xp: f.rewards.xp.total })}>
          +{formatNumber(total)}
        </span>
      </div>
    </div>
  );
}

function LevelProgress({ f, t01 }: { f: Rewarded; t01: number }) {
  const { t } = useTranslation();
  const r = f.rewards;
  const gained = Math.round(r.xp.total * t01);
  const before = xpAtLevelStart(r.levelBefore.level) + r.levelBefore.into;
  const L = levelFromXp(before + gained);
  const need = L.maxed ? 1 : L.needed;
  const lvPrev = L.level === r.levelBefore.level ? r.levelBefore.into : 0;
  const lvNow = L.maxed ? need : L.into;
  const leveled = L.level > r.levelBefore.level;
  const sb = r.skillBarBefore;
  const S = addSkillXp(sb, gained);
  const spNeed = nextSkillPointCost(S.pointsEarned);
  const spPrev = S.pointsEarned === sb.pointsEarned ? sb.barXp : 0;
  const got = S.pointsEarned - sb.pointsEarned;
  return (
    <div className={[ui.glass, s.lvCard, leveled ? s.lvUp : got > 0 ? s.lvPoint : ''].join(' ')}>
      <div className={s.lvRow}>
        <span className={s.lvChips}>
          <span className={ui.chip}>{t('screens.result.lvl', { level: L.level })}</span>
          {leveled && <span className={s.lvUpChip}>{t('screens.result.levelUp')}</span>}
        </span>
        <span className={s.lvXp}>
          {L.maxed ? t('screens.level.max') : t('screens.level.xp', { now: formatNumber(L.into), need: formatNumber(L.needed) })}
        </span>
      </div>
      <SplitBar kind="xp" className={s.lvBar} prev={lvPrev / need} gain={(lvNow - lvPrev) / need} label={t('screens.level.title')} />
      <SplitBar kind="sp" className={s.spBar} prev={spPrev / spNeed} gain={(S.barXp - spPrev) / spNeed} label={t('screens.level.spLabel')} />
      <div className={s.spRow}>
        <span className={s.spText}>
          <Trans
            i18nKey="screens.level.nextPoint"
            values={{ now: formatNumber(S.barXp), need: formatNumber(spNeed) }}
            components={{ g: <span className={s.green} /> }}
          />
        </span>
        {got > 0 && <span className={s.spChip}>{t('screens.result.pointChip', { count: got })}</span>}
      </div>
    </div>
  );
}

function CoinsCard({ f, t01 }: { f: Rewarded; t01: number }) {
  const { t } = useTranslation();
  const wallet = useApp((st) => st.progress.coins);
  const coins = f.rewards.coins + f.rewards.overflowCoins;
  return (
    <div className={[ui.glass, s.coins].join(' ')}>
      <div className={s.coinsHead}>
        <span>{t('screens.result.coins')}</span>
        <span className={s.coinsTotal}>{t('screens.result.coinsTotal', { total: formatNumber(wallet) })}</span>
      </div>
      <div className={s.coinsVal}>
        <span className={ui.coin} aria-hidden="true" />+{formatNumber(Math.round(coins * t01))}
      </div>
    </div>
  );
}

/** In place of the rewards: practice (offline), or not confirmed by the server yet. */
function NoRewards({ practice }: { practice: boolean }) {
  const { t } = useTranslation();
  const key = practice ? 'practice' : 'pending';
  return (
    <div className={s.tip} role="status">
      <div className={s.tipTitle}>{t(`screens.result.${key}.title`)}</div>
      <div className={s.tipBody}>{t(`screens.result.${key}.body`)}</div>
    </div>
  );
}

function TipCard({ f }: { f: FightSummary }) {
  const { t } = useTranslation();
  const points = useApp((st) => availablePoints(st.progress));
  const id = points > 0 ? 'training' : TIPS[Math.abs(f.match.seed) % TIPS.length]!;
  return (
    <div className={s.tip}>
      <div className={s.tipLabel}>{t('screens.result.tip')}</div>
      <div className={s.tipTitle}>{t(`screens.result.tips.${id}.title`)}</div>
      <div className={s.tipBody}>{t(`screens.result.tips.${id}.body`, { name: f.match.opponent.name, count: points })}</div>
    </div>
  );
}

/** What the player's Power and Takedown added to their damage in this fight (D48), when anything. */
function SkillDamageCard({ f }: { f: FightSummary }) {
  const { t } = useTranslation();
  const mine = f.result.skillDamage?.[0];
  const items = mine
    ? SKILL_DAMAGE.filter((k) => Math.round(mine[k]) > 0).map((k) =>
        t('screens.result.skillDamage.item', { skill: t(`screens.skills.${k}.name`), damage: formatNumber(Math.round(mine[k])) }),
      )
    : [];
  if (!items.length) return null;
  return (
    <div className={[ui.card, s.skillCard].join(' ')}>
      <span className={ui.label}>{t('screens.result.skillDamage.title')}</span>
      <span className={s.skillDmg}>{items.join(' · ')}</span>
    </div>
  );
}

const SKILL_DAMAGE = ['power', 'takedown'] as const;

/** A league fight's table points (docs/game-design.md §7.5): win 3, draw 1, loss 0. */
function LeaguePoints({ f }: { f: FightSummary }) {
  const { t } = useTranslation();
  const pts = f.outcome === 'win' ? LEAGUES.points.win : f.outcome === 'draw' ? LEAGUES.points.draw : LEAGUES.points.loss;
  return (
    <div className={[ui.card, s.leagueCard].join(' ')}>
      <span className={ui.label}>{t('screens.result.league')}</span>
      <span className={s.leaguePts}>{t('screens.result.tablePoints', { count: pts })}</span>
    </div>
  );
}

function Buttons({ layout, league }: { layout: LayoutMode; league: boolean }) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const openLeague = useApp((st) => st.openLeague);
  const startQuickFight = useApp((st) => st.startQuickFight);
  const points = useApp((st) => availablePoints(st.progress));
  // A league fight has no rematch: the table is next.
  const again = league ? (
    <button type="button" className={[ui.primary, s.again].join(' ')} onClick={() => openLeague('league')}>
      {t('screens.result.leagueTable')}
      <span className={ui.arrow} aria-hidden="true">
        →
      </span>
    </button>
  ) : (
    <button type="button" className={[ui.primary, s.again].join(' ')} onClick={() => startQuickFight()}>
      {t('screens.result.fightAgain')}
      <span className={ui.arrow} aria-hidden="true">
        ▷
      </span>
    </button>
  );
  const train =
    points > 0 ? (
      <button
        type="button"
        className={[ui.secondary, s.secondaryBtn, s.trainBtn].join(' ')}
        onClick={() => go('training')}
        aria-label={t('screens.result.trainAria', { count: points })}
      >
        <span className={s.trainText}>
          {t('screens.result.train')}
          <span className={ui.badge}>{points}</span>
        </span>
        <span aria-hidden="true">→</span>
      </button>
    ) : null;
  const home = (
    <button type="button" className={[ui.secondary, s.secondaryBtn].join(' ')} onClick={() => go('home')}>
      {t('screens.result.home')}
      <span aria-hidden="true">→</span>
    </button>
  );
  if (layout === 'landscape') {
    return (
      <div className={s.btnRow}>
        {again}
        {train}
        {home}
      </div>
    );
  }
  return (
    <div className={s.btnStack}>
      {again}
      <div className={s.btnRow2}>
        {train}
        {home}
      </div>
    </div>
  );
}

function Frame({ layout, d, children }: { layout: LayoutMode; d: Derived; children: ReactNode }) {
  const reduced = useReducedMotion();
  const shade = d.loss
    ? layout === 'portrait'
      ? 'linear-gradient(180deg,rgba(20,19,18,.9),rgba(20,19,18,.5) 22%,rgba(20,19,18,.98) 40%)'
      : 'linear-gradient(90deg,rgba(20,19,18,.5),rgba(20,19,18,.66) 44%,rgba(20,19,18,.95) 56%)'
    : layout === 'portrait'
      ? 'linear-gradient(180deg,rgba(20,19,18,.85),rgba(20,19,18,.35) 24%,rgba(20,19,18,.97) 48%)'
      : layout === 'desktop'
        ? 'linear-gradient(90deg,rgba(20,19,18,.35),rgba(20,19,18,.55) 55%,rgba(20,19,18,.94) 78%)'
        : 'linear-gradient(90deg,rgba(20,19,18,.3),rgba(20,19,18,.5) 44%,rgba(20,19,18,.93) 56%)';
  return (
    <>
      <ArenaBackdrop tone={d.loss ? 'loss' : 'menu'} shade={shade} />
      {d.win && !reduced && <div className={s.flash} aria-hidden="true" />}
      {children}
    </>
  );
}

export function ResultScreen() {
  const last = useApp((st) => st.lastFight);
  const go = useApp((st) => st.go);
  useScrollTop();
  useEffect(() => {
    if (!last) go('home');
  }, [last, go]);
  return last ? <ResultView last={last} /> : null;
}

function ResultView({ last }: { last: FightSummary }) {
  const { t } = useTranslation();
  const layout = useLayoutMode();
  const t01 = useEaseIn(COUNT_DELAY, COUNT_DURATION);
  const d = derive(last);
  const labels = useLabels(last, d);
  // A short stinger for the outcome (none for a draw).
  useEffect(() => {
    if (last.outcome === 'win') uiSounds().win();
    else if (last.outcome === 'loss') uiSounds().lose();
  }, [last]);

  const bannerClass = [s.banner, d.win ? s.bannerWin : d.draw ? s.bannerDraw : s.bannerLoss, d.ko && d.win ? s.bannerKo : ''].join(' ');
  const hero = (size: 'sm' | 'md' | 'lg') => (
    <div className={[s.hero, d.loss ? s.heroLoss : ''].join(' ')}>
      {d.win && <Rays size={size === 'lg' ? 820 : 430} color="rgba(245,160,60,.16)" top={size === 'lg' ? '52%' : '57%'} />}
      <h1 className={bannerClass}>{labels.banner}</h1>
      <div className={s.method}>{labels.method}</div>
      <Scorecard f={last} />
      <Fighters f={last} d={d} size={size} />
      <div className={s.name}>{last.match.player.name}</div>
      <VsLine f={last} d={d} />
    </div>
  );

  const league = !!last.match.online?.info.league;
  const rewarded = last.rewards ? { ...last, rewards: last.rewards } : null;
  const rewards = rewarded ? (
    <>
      {league && <LeaguePoints f={last} />}
      <SkillDamageCard f={last} />
      <XpBreakdown f={rewarded} t01={t01} />
      <LevelProgress f={rewarded} t01={t01} />
      <CoinsCard f={rewarded} t01={t01} />
      {!league && <KeepProgress />}
    </>
  ) : (
    <>
      <SkillDamageCard f={last} />
      <NoRewards practice={!!last.practice} />
    </>
  );

  if (layout === 'landscape') {
    return (
      <div className={['cg-ui', s.root, s.fixed, s.land].join(' ')}>
        <Frame layout={layout} d={d}>
          <div className={s.landGrid}>
            <section className={s.landLeft}>
              <div className={s.topRow}>
                <span>{labels.mode}</span>
                <span>{labels.meta}</span>
              </div>
              {hero('sm')}
              {d.loss && <TipCard f={last} />}
            </section>
            <aside className={s.landAside} aria-label={t('screens.result.rewards')}>
              {/* The rewards scroll when the screen is short; the buttons always stay in view. */}
              <div className={s.landRewards}>{rewards}</div>
              <Buttons layout={layout} league={league} />
            </aside>
          </div>
        </Frame>
      </div>
    );
  }

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen, s.desk].join(' ')}>
        <DesktopHeader />
        <main className={s.deskMain}>
          <div className={s.deskCard}>
            <Frame layout={layout} d={d}>
              <section className={s.deskLeft}>
                <div className={s.deskMeta}>
                  {labels.mode} · {labels.meta}
                </div>
                {hero('lg')}
                <div className={s.deskSpacer} />
              </section>
              <aside className={s.deskAside} aria-label={t('screens.result.rewards')}>
                <div className={ui.label}>{t('screens.result.rewards')}</div>
                {rewards}
                {d.loss && <TipCard f={last} />}
                <div className={s.deskGrow} />
                <Buttons layout={layout} league={league} />
              </aside>
            </Frame>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={['cg-ui', s.root, s.port].join(' ')}>
      <Frame layout={layout} d={d}>
        <div className={s.portCol}>
          <div className={s.topRow}>
            <span>{labels.mode}</span>
            <span>{labels.meta}</span>
          </div>
          {hero('md')}
          {rewards}
          {d.loss && <TipCard f={last} />}
          <div className={s.portSticky}>
            <Buttons layout={layout} league={league} />
          </div>
        </div>
      </Frame>
    </div>
  );
}
